import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import { applySnapshot, destroy, getParent, getSnapshot, types } from 'mobx-state-tree';
import ReactGA from 'react-ga';
import { NavigateFunction } from 'react-router-dom';
import { GACategories } from 'src/services/Analytics';
import Service from 'src/services/api/Api';
import { CreateOrder, OrderProduct } from 'src/services/api/models';
import Toast from 'src/services/Toasts';
import AnalyticsEcommerce from 'src/utils/analytics-ecommerce';
import {
    FREE_SHIPPING_THRESHOLD,
    OrderMode,
    ProductCategoryTypes,
    SHIPPING_FLAT_RATE,
    userStateDbColumns,
    ViewModeEnum,
} from 'src/utils/constants';
import RootStore from './RootStore';
import { CartItemModel, TCartItem } from './types/CartItemModel';
import { getNextWorkingDay } from 'src/utils/deliveryDateMaths';

dayjs.extend(minMax);

const CartStore = types
    .model('CartStore', {
        items: types.optional(types.array(CartItemModel), []),
        bundleWarning: types.optional(types.boolean, false),
    })
    .volatile(() => ({
        isLoaded: false,
        requestedDeliveryDate: dayjs().toDate(),
        orderMode: OrderMode.BackOrder,
        orderQuotePrice: 0,
    }))
    .views(self => ({
        get listItems() {
            return [...self.items].reverse();
        },
        get selectedItems() {
            return this.listItems.filter(item => item.isSelected);
        },
        get spareParts() {
            return this.listItems.filter(item => item.product.type !== ProductCategoryTypes.BIKE);
        },
        get sparePartsDeliveryDates() {
            return this.spareParts.map(item =>
                item.product!.productInformation.availableFrom
                    ? dayjs(item.product!.productInformation.availableFrom)
                    : dayjs(new Date()),
            );
        },
        get bikesShippingDate(): string {
            let shippingDates = deliveryDates(this.selectedItems, getParent(self, 1));
            if (shippingDates.length > 0) {
                const oldestDate = dayjs.max(shippingDates);
                return `${oldestDate.toDate()}`;
            }
            return '';
        },
        get sparePartsShippingDate(): string {
            let shippingDates = deliveryDates(this.spareParts, getParent(self, 1));
            if (shippingDates.length > 0) {
                const oldestDate = dayjs.max(shippingDates);
                return `${oldestDate.toDate()}`;
            }
            return '';
        },
        get isDesirableDeliveryDateNextYear() {
            const nextYear = dayjs().year() + 1;
            const desirableYear = dayjs(new Date(this.bikesShippingDate)).year();
            return desirableYear === nextYear;
        },
        filterItems(listTypes: ProductCategoryTypes[]) {
            return this.listItems.filter(item => listTypes.includes(item.product.type));
        },
        filterItemsByCartType(activeCartType: ProductCategoryTypes.BIKE | ProductCategoryTypes.PART) {
            const allowedItemTypes =
                activeCartType === ProductCategoryTypes.BIKE
                    ? [ProductCategoryTypes.BIKE]
                    : [ProductCategoryTypes.PART, ProductCategoryTypes.BATTERY, ProductCategoryTypes.MARKETING];
            return this.listItems.filter(item => {
                if (activeCartType === ProductCategoryTypes.BIKE) {
                    return item.isSelected && allowedItemTypes.includes(item.product.type);
                }
                return allowedItemTypes.includes(item.product.type);
            });
        },
    }))
    .views(self => ({
        isCartEmpty(activeCartType: ProductCategoryTypes.BIKE | ProductCategoryTypes.PART): boolean {
            if (activeCartType === ProductCategoryTypes.BIKE) {
                return (
                    self.selectedItems.filter(
                        item =>
                            activeCartType ===
                            (item.product.type === ProductCategoryTypes.BIKE
                                ? item.product.type
                                : ProductCategoryTypes.PART),
                    ).length === 0
                );
            }
            return (
                self.listItems.filter(
                    item =>
                        activeCartType ===
                        (item.product.type === ProductCategoryTypes.BIKE
                            ? item.product.type
                            : ProductCategoryTypes.PART),
                ).length === 0
            );
        },
        get totalItemsQuantity(): number {
            return self.listItems.length > 0
                ? self.listItems.map(item => item.quantity).reduce((acc, quantity) => acc + quantity, 0)
                : 0;
        },
        get totalBikesQuantity(): number {
            return self.listItems.length > 0
                ? self.listItems
                      .filter(item => item.product.type === ProductCategoryTypes.BIKE)
                      .map(item => item.quantity)
                      .reduce((acc, quantity) => acc + quantity, 0)
                : 0;
        },
        get totalSparePartsQuantity(): number {
            return self.listItems.length > 0
                ? self.listItems
                      .filter(item => item.product.type !== ProductCategoryTypes.BIKE)
                      .map(item => item.quantity)
                      .reduce((acc, quantity) => acc + quantity, 0)
                : 0;
        },
        totalPrice(activeCartType: ProductCategoryTypes.BIKE | ProductCategoryTypes.PART): number {
            const isActiveCartType = (type: ProductCategoryTypes) => {
                const itemType =
                    type === ProductCategoryTypes.BIKE ? ProductCategoryTypes.BIKE : ProductCategoryTypes.PART;
                return activeCartType === itemType;
            };

            const cartItems = activeCartType === ProductCategoryTypes.BIKE ? self.selectedItems : self.items;
            const filteredByType = cartItems.filter(item => isActiveCartType(item.product.type));
            return filteredByType.length > 0
                ? filteredByType
                      .map(item => (item.product ? item.totalItemsPrice(self.isDesirableDeliveryDateNextYear) : 0))
                      .reduce((acc, price) => acc + price)
                : 0;
        },
        totalPriceInclusive(): number {
            return self.items.length > 0
                ? self.items
                      .map(item => (item.product ? item.totalItemsPrice(self.isDesirableDeliveryDateNextYear) : 0))
                      .reduce((acc, price) => acc + price)
                : 0;
        },
        get totalBikesInCart(): number {
            return self.selectedItems.reduce((acc, item) => {
                if (item.product.type === ProductCategoryTypes.BIKE) {
                    return acc + item.quantity;
                }
                return acc + 0;
            }, 0);
        },
        get totalBikesInCartInclusive(): number {
            return self.listItems.reduce((acc, item) => {
                if (item.product.type === ProductCategoryTypes.BIKE) {
                    return acc + item.quantity;
                }
                return acc + 0;
            }, 0);
        },
        getCartProducts(activeCartType: ProductCategoryTypes.BIKE | ProductCategoryTypes.PART): OrderProduct[] {
            const products: OrderProduct[] = [];
            self.filterItemsByCartType(activeCartType).forEach((item: TCartItem) => {
                if (item.product.parentCode) {
                    products.push(
                        createOrderProduct(
                            item.product!.id,
                            item.quantity,
                            item.reference,
                            item.product.parentCode,
                            item.product.parentFrameNumber || '',
                        ),
                    );
                } else {
                    products.push(createOrderProduct(item.product!.id, item.quantity, item.reference));
                }
                if (item.battery && item.battery.code && item.battery.code !== 'N/A') {
                    products.push(createOrderProduct(item.battery.code, item.quantity, item.reference));
                }
                if (item.saddleAndGrip) {
                    products.push(createOrderProduct(item.saddleAndGrip, item.quantity, item.reference));
                }
                if (item.strap) {
                    products.push(createOrderProduct(item.strap, item.quantity, item.reference));
                }
            });
            return products;
        },
    }))
    .actions(self => ({
        setLoaded(value: boolean) {
            self.isLoaded = value;
        },

        setOrderQuotePrice(price: number) {
            self.orderQuotePrice = Number(price.toFixed(2));
        },

        setRequestedDeliveryDate(newDate: Date) {
            self.requestedDeliveryDate = newDate;
        },

        setOrderMode(newMode: OrderMode) {
            self.orderMode = newMode;
        },

        async getOrderQuote(): Promise<any> {
            if (self.listItems.length < 1) {
                this.setOrderQuotePrice(0);
                return;
            }

            this.setLoaded(false);
            const rootStore: typeof RootStore.Type = getParent(self, 1);
            const orderObject = createOrderObject({
                listItems: self.listItems,
                deliveryDate: self.requestedDeliveryDate,
                orderMode: self.orderMode,
            });

            try {
                const response = await Service.getOrderQuote(orderObject);
                if (rootStore.config.viewMode === ViewModeEnum.DEALER) {
                    this.setOrderQuotePrice(response.data?.totalPriceForDealer || 0);
                } else {
                    this.setOrderQuotePrice(response.data?.totalPriceForConsumer || 0);
                }
            } catch (e: any) {
                console.error(e);
            } finally {
                this.setLoaded(true);
            }
        },

        async removeCartItem(item: TCartItem, sync: boolean = true) {
            try {
                // Suppressing this operation separately because a faulty cart item can break this process in some rare cases
                // Read before destroy(item) below — item.product is unreadable once the node is detached/dead
                const { product, quantity } = item;

                // Sending item removed from cart event to GA
                AnalyticsEcommerce.getInstance()
                    .clear()
                    .removeProductFromCart({
                        id: product.id,
                        name: product.name,
                        list: 'Shopping Cart',
                        brand: 'QWIC',
                        category: `${product.serie}/${product.model}`,
                        variant: product.productInformation?.color || undefined,
                        price: product.getPrice(),
                        quantity: quantity,
                    })
                    .sendPageView();
                // eslint-disable-next-line no-empty
            } catch (ignored) {}

            destroy(item);

            // In some cases we don't want to send the request to update the storage immediately
            if (sync) {
                try {
                    await Service.updateStorageData(userStateDbColumns.cart, JSON.stringify(getSnapshot(self)));
                    await this.getOrderQuote();
                } catch (e) {
                    console.error(e);
                    Toast.PreferencesError();
                }
            }
        },
    }))
    .actions(self => ({
        async cleanCartItems(activeCartType?: ProductCategoryTypes.BIKE | ProductCategoryTypes.PART) {
            if (activeCartType) {
                self.filterItemsByCartType(activeCartType).forEach(CartItem => {
                    if (CartItemModel.is(CartItem)) {
                        self.removeCartItem(CartItem, false);
                    }
                });
            } else {
                self.listItems.forEach(CartItem => {
                    if (CartItemModel.is(CartItem)) {
                        self.removeCartItem(CartItem, false);
                    }
                });
            }

            // Update storage only when all the items have been deleted
            this.updateCartDBSnapshot();
        },
        async addCartItem(newCartItem: TCartItem, quantity: number = 1) {
            let foundCartItem;
            const shouldIncreaseItem = !newCartItem.strap && !newCartItem.saddleAndGrip;
            if (self.items.length > 0) {
                foundCartItem = self.items.find(item => {
                    // Brekr item with configurable battery (B4000, F250)
                    if (item.product && item.brekrBattery && item.frame && !item.carrier) {
                        return (
                            item.product.id === newCartItem.product!.id &&
                            item.brekrBattery === newCartItem.brekrBattery &&
                            item.frame === newCartItem.frame
                        );
                        // Brekr item with battery options and carrier (L250)
                    } else if (item.product && item.battery?.code && item.frame && item.carrier) {
                        return (
                            item.product.id === newCartItem.product!.id &&
                            item.battery.code === newCartItem.battery!.code &&
                            item.frame === newCartItem.frame &&
                            item.carrier === newCartItem.carrier
                        );
                        // Brekr item with battery options + frame, no carrier (F250)
                    } else if (item.product && item.battery?.code && item.frame && !item.carrier) {
                        return (
                            item.product.id === newCartItem.product!.id &&
                            item.battery.code === newCartItem.battery!.code &&
                            item.frame === newCartItem.frame
                        );
                        // Qwic items
                    } else if (item.product && item.battery?.code && !item.saddleAndGrip && !item.strap) {
                        return (
                            item.product.id === newCartItem.product!.id &&
                            item.battery.code === newCartItem.battery!.code &&
                            item.product.productInformation.color === newCartItem.product!.productInformation.color &&
                            item.reference === newCartItem.reference
                        );
                    } else if (item.product && !item.saddleAndGrip && !item.strap) {
                        return (
                            item.product.id === newCartItem.product!.id &&
                            item.product.productInformation.color === newCartItem.product!.productInformation.color &&
                            item.reference === newCartItem.reference
                        );
                    }
                    return false;
                });
            }

            if (shouldIncreaseItem && foundCartItem) {
                foundCartItem.increaseQuantity(quantity);
            } else {
                if (newCartItem.product.parentFrameNumber) {
                    newCartItem.parentFrameNumber = newCartItem.product.parentFrameNumber;
                }
                self.items.push(newCartItem);
            }
            this.updateCartDBSnapshot();
            if (newCartItem) {
                const parent: typeof RootStore.Type = getParent(self);
                Toast.addItem(newCartItem.product.name, () => {
                    ReactGA.event({
                        category: GACategories.UI,
                        action: 'User opens the cart by clicking the toast',
                    });
                    parent.config.toggleCart();
                });
            }
        },
        async updateCartItem(cartItem: TCartItem, quantity: number) {
            const foundCartItem = self.items.find(item => {
                if (item.product && item.brekrBattery && item.frame && !item.carrier) {
                    return (
                        item.product.id === cartItem.product!.id &&
                        item.brekrBattery === cartItem.brekrBattery &&
                        item.frame === cartItem.frame
                    );
                } else if (item.product && item.battery?.code && item.frame && item.carrier) {
                    return (
                        item.product.id === cartItem.product!.id &&
                        item.battery.code === cartItem.battery!.code &&
                        item.frame === cartItem.frame &&
                        item.carrier === cartItem.carrier
                    );
                } else if (item.product && item.battery?.code && item.frame && !item.carrier) {
                    return (
                        item.product.id === cartItem.product!.id &&
                        item.battery.code === cartItem.battery!.code &&
                        item.frame === cartItem.frame
                    );
                } else if (item.product && item.battery) {
                    return (
                        item.product.id === cartItem.product!.id &&
                        item.battery.code === cartItem.battery!.code &&
                        item.product.productInformation.color === cartItem.product!.productInformation.color
                    );
                } else if (item.product) {
                    return (
                        item.product.id === cartItem.product!.id &&
                        item.product.productInformation.color === cartItem.product!.productInformation.color
                    );
                }
                return false;
            });
            if (foundCartItem) {
                foundCartItem.setQuantity(quantity);
                this.updateCartDBSnapshot();
                self.getOrderQuote();
            }
        },
        async updateCartDBSnapshot() {
            try {
                await Service.updateStorageData(userStateDbColumns.cart, JSON.stringify(getSnapshot(self)));
            } catch (e) {
                console.error(e);
                Toast.PreferencesError();
            }
        },
        async updateCartFromDb(snapshot: string) {
            try {
                const rootStore: typeof RootStore.Type = getParent(self, 1);
                const data = JSON.parse(snapshot);

                applySnapshot(self, data);

                for (const item of self.items) {
                    try {
                        // Read the raw stored product id from the snapshot rather than `item.product?.id` —
                        // the latter forces MST to resolve the reference and throws if the product hasn't
                        // been loaded into the tree yet (which is exactly what this code is checking for).
                        const productId = getSnapshot(item).product;
                        const loadBikeModelData = item?.type === ProductCategoryTypes.BIKE;

                        if (loadBikeModelData) {
                            // load configurable parts for bikes in cart
                            await rootStore.products.getConfigurablePartsByProductCode(productId);
                        }

                        const loadSparePartModelData =
                            (rootStore.products.bikes.size && !rootStore.products.bikes.has(productId)) ||
                            item?.type === ProductCategoryTypes.BATTERY ||
                            item?.type === ProductCategoryTypes.PART ||
                            item?.type === ProductCategoryTypes.MARKETING;

                        if (loadSparePartModelData) {
                            await item.loadSparePartInModel();
                            // If it's a sparepart and the parent bike still exists but the spare part itself doesn't
                            if (
                                !rootStore.spareParts.list.has(item.id) &&
                                !rootStore.marketingMaterials.list.has(item.id)
                            ) {
                                self.removeCartItem(item);
                                continue;
                            }
                        }
                    } catch (itemError) {
                        // A single corrupted/unresolvable cart item shouldn't break restoring the rest of the cart
                        console.error(itemError);
                        self.removeCartItem(item);
                    }
                }

                self.setLoaded(true);
            } catch (e) {
                console.error(e);
                Toast.PreferencesError();
            }
        },
        async placeOrder(
            deliveryDate: Date,
            activeCartType: ProductCategoryTypes.BIKE | ProductCategoryTypes.PART,
            navigate: NavigateFunction,
            orderMode: OrderMode,
            orderComment: string = '',
        ): Promise<any> {
            const Products = self.getCartProducts(activeCartType);
            const rootStore: typeof RootStore.Type = getParent(self, 1);
            const OrderType = activeCartType === ProductCategoryTypes.BIKE ? 'Bike' : 'Part';
            let orderObject = {} as CreateOrder;
            if (activeCartType === ProductCategoryTypes.BIKE) {
                const RequestedDeliveryDate = dayjs(deliveryDate).format('YYYY-MM-DD');
                orderObject = {
                    RequestedDeliveryDate,
                    OrderType,
                    Products,
                };
            } else if (activeCartType === ProductCategoryTypes.PART) {
                const RequestedDeliveryDate = dayjs(new Date()).format('YYYY-MM-DD');
                orderObject = {
                    OrderType,
                    Products,
                    RequestedDeliveryDate,
                };
            }

            // QWIC 2.0 non-bike order customizations
            if (
                activeCartType !== ProductCategoryTypes.BIKE &&
                self.totalPrice(activeCartType) < FREE_SHIPPING_THRESHOLD
            ) {
                orderObject.DeliveryFee = SHIPPING_FLAT_RATE;
            }

            // Back Order / Pre Order
            orderObject.OrderMode = orderMode;
            orderObject.OrderComment = orderComment;

            try {
                if (orderObject.OrderType) {
                    const response = await Service.placeOrder(orderObject);
                    if (response && response.status === 201) {
                        this.cleanCartItems(activeCartType);
                        rootStore.config.toggleCart();
                        navigate('/thank-you');
                    } else {
                        Toast.GeneralError();
                    }
                } else {
                    await new Promise((resolve, reject) => {
                        reject(`Please check order shape ${orderObject}`);
                    });
                }
            } catch (e: any) {
                console.error(e.response);
                handlePlaceOrderError(e.response);
            }
        },

        async placeOrderInclusive(
            deliveryDate: Date,
            navigate: NavigateFunction,
            orderMode: OrderMode,
            orderComment: string = '',
        ): Promise<any> {
            const rootStore: typeof RootStore.Type = getParent(self, 1);

            const orderObject = createOrderObject({ listItems: self.listItems, deliveryDate, orderMode, orderComment });

            try {
                const response = await Service.placeOrder(orderObject);
                if (response && response.status === 201) {
                    this.cleanCartItems();
                    rootStore.config.toggleCart();
                    navigate('/thank-you');
                } else {
                    Toast.GeneralError();
                }
            } catch (e: any) {
                console.error(e);
                handlePlaceOrderError(e.response);
            }
        },
        toggleBundleWarning(toggle: boolean = self.totalBikesInCart > 1) {
            self.bundleWarning = toggle;
        },
    }))
    .views(self => ({
        get earliestShippingDate(): Date {
            let earliestShippingDate = dayjs().toDate();
            const shippingDates = deliveryDates(self.listItems, getParent(self, 1));
            if (shippingDates.length > 0) {
                const oldestDate = dayjs.max(shippingDates);
                earliestShippingDate = oldestDate.toDate();
            }

            self.setRequestedDeliveryDate(getNextWorkingDay(earliestShippingDate));

            return getNextWorkingDay(earliestShippingDate);
        },
    }));

// We have to manually parse the error message because of the way backend handles thrown exceptions.
// One of the solutions would be to return the error as a regular payload but with an error HTTP status code.
// Another solution would be to fix the backend exception handler to properly return the structured data.
const handlePlaceOrderError = (response: any) => {
    const errorMessage = String(response?.data?.detail ?? '');

    const productCodesRegexp = new RegExp(/:\s*([0-9A-Z,\s]+)$/);

    if (errorMessage.match(/BC products blocked or missing/)) {
        Toast.CreateOrderProblemWithProduct(errorMessage.match(productCodesRegexp)?.[1]);
    } else if (errorMessage.match(/BC products out of stock/)) {
        Toast.CreateOrderProblemWithProduct(errorMessage.match(productCodesRegexp)?.[1]);
    } else {
        Toast.CreateOrderProblemWithAccount();
    }
};

const deliveryDates = (items: TCartItem[], rootStore: typeof RootStore.Type) => {
    return items.map(item => {
        let date = item.product!.productInformation.availableFrom
            ? dayjs(item.product!.productInformation.availableFrom)
            : dayjs(new Date());
        if (item.battery) {
            // if bike has a battery attached, check battery availability date
            const batteryInfo = item.product.batteryInfo(item.battery!.code);
            if (batteryInfo && dayjs(batteryInfo.availableFrom || '').isAfter(date)) {
                date = dayjs(batteryInfo.availableFrom || '');
            }
        }
        if (item.brekrBattery) {
            // if bike has a brekr battery attached, check brekr battery availability date
            const brekrBatteryInfo = rootStore.products.getConfigurablePartById(item.id, item.brekrBattery);
            if (brekrBatteryInfo && dayjs(brekrBatteryInfo.availableFrom || '').isAfter(date)) {
                date = dayjs(brekrBatteryInfo.availableFrom || '');
            }
        }
        if (item.frame) {
            // if bike has a frame attached, check frame availability date
            const frameInfo = rootStore.products.getConfigurablePartById(item.id, item.frame);
            if (frameInfo && dayjs(frameInfo.availableFrom || '').isAfter(date)) {
                date = dayjs(frameInfo.availableFrom || '');
            }
        }
        return date;
    });
};

const createOrderProduct = (
    productCode: string,
    quantity: number,
    reference: string,
    ParentProductCode?: string,
    FrameNumber?: string,
) => {
    return ParentProductCode
        ? {
              ProductCode: productCode,
              Quantity: quantity,
              Reference: reference,
              ParentProductCode,
              FrameNumber,
          }
        : {
              ProductCode: productCode,
              Quantity: quantity,
              Reference: reference,
          };
};

const createOrderObject = ({
    listItems,
    deliveryDate,
    orderMode,
    orderComment = '',
}: {
    listItems: TCartItem[];
    deliveryDate: Date;
    orderMode: OrderMode;
    orderComment?: string;
}): CreateOrder => {
    const Products: OrderProduct[] = [];
    listItems.forEach((item: TCartItem) => {
        Products.push(createOrderProduct(item.product!.id, item.quantity, item.reference));
        if (item.brekrBattery) {
            const batteryQuantityMultiplier = item.isDoubleBatterySelected ? 2 : 1;
            Products.push(createOrderProduct(item.brekrBattery, item.quantity * batteryQuantityMultiplier, item.reference, item.product!.id));
            // if (item.isDoubleBatterySelected) {
            //     item.doubleBatteryListIds.forEach((code: string) => {
            //         Products.push(createOrderProduct(code, item.quantity, item.reference, item.product!.id));
            //     });
            // }
        }
        if (item.battery?.code) {
            Products.push(createOrderProduct(item.battery.code, item.quantity, item.reference, item.product!.id));
        }
        if (item.frame) {
            Products.push(createOrderProduct(item.frame, item.quantity, item.reference, item.product!.id));
        }
        if (item.carrier) {
            Products.push(createOrderProduct(item.carrier, item.quantity, item.reference, item.product!.id));
        }
    });

    let orderObject = {} as CreateOrder;

    const RequestedDeliveryDate = dayjs(deliveryDate).format('YYYY-MM-DD');
    orderObject = {
        RequestedDeliveryDate,
        Products,
    };

    // Back Order / Pre Order
    orderObject.OrderMode = orderMode;
    orderObject.OrderComment = orderComment;

    return orderObject;
};

export default CartStore;
